import { createFileRoute } from '@tanstack/react-router'
import {
  IconArrowDownToLineOutline18,
  IconCheckOutline18,
  IconPlayRotateClockwiseOutline18,
} from 'nucleo-ui-outline-18'
import { type ReactNode, useState } from 'react'
import { AiSection } from '~/components/brand/ai-section'
import { AnimatedMark, type BrandMotionVariant } from '~/components/brand/animated-mark'
import { MarketingShell } from '~/components/marketing/marketing-shell'
import { cn } from '~/lib/utils'

const PAGE_TITLE = 'Marca — Planno'

export const Route = createFileRoute('/brand')({
  component: BrandPage,
  head: () => ({
    // Internal brand-kit page: kept out of search results on purpose.
    meta: [{ title: PAGE_TITLE }, { content: 'noindex,follow', name: 'robots' }],
  }),
})

const DownloadLink = ({ href, label }: { href: string; label: string }) => (
  <a
    className="inline-flex items-center gap-1.5 rounded-full bg-[#f0f0f1] px-3.5 py-1.5 font-semibold text-[#0b0b0d] text-[13px] transition hover:bg-[#e7e7e8]"
    download
    href={href}
  >
    <IconArrowDownToLineOutline18 className="size-3.5" />
    {label}
  </a>
)

const Section = ({
  children,
  intro,
  title,
}: {
  children: ReactNode
  intro?: string
  title: string
}) => (
  <section className="border-[#ececec] border-t py-16">
    <div className="mx-auto max-w-[1120px] px-6">
      <h2 className="font-display text-[#0b0b0d] text-[clamp(26px,3.4vw,34px)] leading-[1.1] tracking-[-0.01em]">
        {title}
      </h2>
      {intro ? (
        <p className="mt-3 max-w-[640px] text-[#6c6c72] text-[16px] leading-relaxed">{intro}</p>
      ) : null}
      <div className="mt-10">{children}</div>
    </div>
  </section>
)

type LockupCardProps = {
  darkSrc: string
  description: string
  downloads: { href: string; label: string }[]
  /** Intrinsic SVG dimensions, for aspect ratio (CSS controls display size). */
  intrinsic: { height: number; width: number }
  lightSrc: string
  name: string
  /** Tailwind height class for the preview image. */
  previewClass: string
}

const LockupCard = ({
  darkSrc,
  description,
  downloads,
  intrinsic,
  lightSrc,
  name,
  previewClass,
}: LockupCardProps) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec]">
    <div className="flex h-[180px] items-center justify-center bg-[#fafafa] px-8">
      <img
        alt={`Logotipo Planno — ${name}, versão escura`}
        className={cn(previewClass, 'w-auto')}
        height={intrinsic.height}
        src={lightSrc}
        width={intrinsic.width}
      />
    </div>
    <div className="flex h-[120px] items-center justify-center bg-[#0b0b0d] px-8">
      <img
        alt={`Logotipo Planno — ${name}, versão branca`}
        className={cn(previewClass, 'max-h-[80px] w-auto')}
        height={intrinsic.height}
        src={darkSrc}
        width={intrinsic.width}
      />
    </div>
    <div className="p-5">
      <h3 className="font-semibold text-[#0b0b0d] text-[15px]">{name}</h3>
      <p className="mt-1 text-[#6c6c72] text-[13.5px] leading-relaxed">{description}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {downloads.map((d) => (
          <DownloadLink href={d.href} key={d.href} label={d.label} />
        ))}
      </div>
    </div>
  </div>
)

const BRAND_COLORS = [
  {
    hex: '#0b0b0d',
    name: 'Tinta',
    role: 'A cor da marca e do produto. Logotipo, títulos, botões e ações — o primário é preto.',
    swatchClass: 'bg-[#0b0b0d]',
  },
  {
    hex: '#ffffff',
    name: 'Branco',
    role: 'Fundos. O logotipo branco só aparece sobre fundos escuros.',
    swatchClass: 'border border-[#ececec] bg-white',
  },
  {
    hex: '#f8f8fc',
    name: 'Canvas',
    role: 'Fundo das áreas de trabalho dentro do produto.',
    swatchClass: 'border border-[#ececec] bg-[#f8f8fc]',
  },
  {
    hex: '#6c6c72',
    name: 'Cinza',
    role: 'Texto de apoio e legendas.',
    swatchClass: 'bg-[#6c6c72]',
  },
  {
    hex: '#3181F8',
    name: 'Azul',
    role: 'O único acento fixo. Marca o que está selecionado — caixas marcadas, dias escolhidos, filtros ativos. Nunca decorativo.',
    swatchClass: 'bg-[#3181F8]',
  },
]

const ColorCard = ({ hex, name, role, swatchClass }: (typeof BRAND_COLORS)[number]) => {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(hex)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[#ececec]">
      <div className={cn('-m-1 h-[96px]', swatchClass)} />
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold text-[#0b0b0d] text-[14px]">{name}</h3>
          <button
            className="inline-flex items-center gap-1 rounded-full bg-[#f0f0f1] px-2.5 py-1 font-medium font-mono text-[#3d3d42] text-[12px] transition hover:bg-[#e7e7e8]"
            onClick={copy}
            type="button"
          >
            {copied ? <IconCheckOutline18 className="size-3" /> : null}
            {copied ? 'Copiado' : hex.toUpperCase()}
          </button>
        </div>
        <p className="mt-1.5 text-[#6c6c72] text-[12.5px] leading-relaxed">{role}</p>
      </div>
    </div>
  )
}

const FORMAT_GUIDES = [
  {
    body: 'Use o símbolo sozinho, centralizado, com respiro generoso. É o formato de avatares, favicons, ícones de app e qualquer espaço 1:1.',
    example: (
      <img
        alt="Ícone quadrado do Planno"
        className="size-[96px] rounded-[22px] border border-[#ececec]"
        height={1024}
        src="/brand/planno-icon-square-light.svg"
        width={1024}
      />
    ),
    name: 'Quadrado',
    spec: 'Símbolo ocupando ~60% da largura do quadrado, sempre centralizado.',
  },
  {
    body: 'Símbolo sobre o nome, ambos centralizados. Para espaços altos e estreitos: stories, capas verticais, materiais impressos em retrato.',
    example: (
      <img
        alt="Logotipo vertical do Planno"
        className="h-[110px] w-auto"
        height={708}
        src="/brand/planno-logo-vertical.svg"
        width={758}
      />
    ),
    name: 'Vertical',
    spec: 'Só use quando o horizontal não couber com conforto.',
  },
  {
    body: 'Símbolo à esquerda do nome. É o formato padrão da marca — cabeçalhos, assinaturas de e-mail, rodapés, apresentações e parcerias.',
    example: (
      <img
        alt="Logotipo horizontal do Planno"
        className="h-[44px] w-auto"
        height={260}
        src="/brand/planno-logo-horizontal.svg"
        width={1088}
      />
    ),
    name: 'Horizontal',
    spec: 'Na dúvida, use este.',
  },
]

type MotionStudy = {
  body: string
  name: string
  /** Where this treatment would earn its place, if it were adopted. */
  use: string
  variant: BrandMotionVariant
}

const MOTION_STUDIES: MotionStudy[] = [
  {
    body: 'Os dois picos sobem do horizonte, o da direita meio passo atrás. É o movimento mais próximo do sentido do símbolo: terreno que se levanta.',
    name: 'Ascensão',
    use: 'Abertura de página, primeira carga do produto.',
    variant: 'rise',
  },
  {
    body: 'Uma passagem de luz atravessa a marca da esquerda para a direita, na mesma inclinação das encostas.',
    name: 'Horizonte',
    use: 'Transições curtas, cabeçalhos que entram com a rolagem.',
    variant: 'wipe',
  },
  {
    body: 'O contorno se desenha e a marca preenche no fim — a montanha sendo riscada antes de existir.',
    name: 'Traço',
    use: 'Vídeo e apresentações, onde há tempo para respirar.',
    variant: 'draw',
  },
  {
    body: 'Os dois picos chegam de lados opostos e assentam juntos. Duas partes que só fazem sentido no mesmo lugar.',
    name: 'Encaixe',
    use: 'Onboarding, telas de conclusão.',
    variant: 'assemble',
  },
  {
    body: 'As encostas se levantam da base, como um mapa que se abre sobre a mesa.',
    name: 'Dobra',
    use: 'Estados vazios, cartões que aparecem em sequência.',
    variant: 'unfold',
  },
  {
    body: 'O único que não termina: os picos respiram alternados enquanto algo carrega.',
    name: 'Pulso',
    use: 'Carregamento e espera — no lugar de um spinner genérico.',
    variant: 'pulse',
  },
]

/* Each study replays on hover and on click; `playKey` remounts the mark,
 * which is what restarts a CSS animation. `roundKey` comes from the section
 * so "reproduzir todas" can restart every card at once. */
const MotionStudyCard = ({ roundKey, study }: { roundKey: number; study: MotionStudy }) => {
  const [playKey, setPlayKey] = useState(0)
  const replay = () => setPlayKey((n) => n + 1)

  /* Hovering anywhere on the card replays it; the Rever button is the same
   * affordance for keyboard and pointer-less input. The card itself stays a
   * plain container — a <button> can't legally wrap a heading, and it would
   * also flatten the whole card into one accessible name. */
  return (
    <div
      className="group flex flex-col rounded-2xl border border-[#ececec] p-5 transition hover:border-[#d9d9de]"
      onMouseEnter={replay}
    >
      <div className="flex h-[168px] shrink-0 items-center justify-center rounded-xl bg-[#fafafa]">
        <AnimatedMark key={`${roundKey}-${playKey}`} size={64} variant={study.variant} />
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <h3 className="font-semibold text-[#0b0b0d] text-[15px]">{study.name}</h3>
        <button
          aria-label={`Rever a animação ${study.name}`}
          className="inline-flex items-center gap-1 text-[#9a9aa1] text-[12px] transition hover:text-[#3d3d42] group-hover:text-[#3d3d42]"
          onClick={replay}
          type="button"
        >
          <IconPlayRotateClockwiseOutline18 className="size-3.5" />
          Rever
        </button>
      </div>
      <p className="mt-1.5 text-[#6c6c72] text-[13.5px] leading-relaxed">{study.body}</p>
      <p className="mt-3 text-[#0b0b0d] text-[13px]">{study.use}</p>
    </div>
  )
}

/** The three strongest candidates, at the scale they'd actually run. */
const LOCKUP_STUDIES: BrandMotionVariant[] = ['rise', 'wipe', 'draw']

const PlayAllButton = ({ onPlay }: { onPlay: () => void }) => (
  <button
    className="inline-flex items-center gap-2 rounded-full bg-[#f0f0f1] px-4 py-2 font-semibold text-[#0b0b0d] text-[13px] transition hover:bg-[#e7e7e8]"
    onClick={onPlay}
    type="button"
  >
    <IconPlayRotateClockwiseOutline18 className="size-3.5" />
    Reproduzir todas
  </button>
)

const MotionSection = () => {
  const [roundKey, setRoundKey] = useState(0)
  const playAll = () => setRoundKey((n) => n + 1)

  return (
    <>
      <div className="-mt-6 mb-8 flex justify-start">
        <PlayAllButton onPlay={playAll} />
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {MOTION_STUDIES.map((study) => (
          <MotionStudyCard key={study.variant} roundKey={roundKey} study={study} />
        ))}
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#ececec]">
        <div className="grid divide-y divide-[#26262b] bg-[#0b0b0d] sm:divide-x sm:divide-y-0 md:grid-cols-3">
          {LOCKUP_STUDIES.map((variant) => (
            <div className="flex h-[150px] items-center justify-center px-6" key={variant}>
              <AnimatedMark key={roundKey} size={30} tone="white" variant={variant} withWordmark />
            </div>
          ))}
        </div>
        <p className="border-[#ececec] border-t px-5 py-3 text-[#6c6c72] text-[12.5px]">
          Ascensão, Horizonte e Traço com o nome, no tamanho de uma tela de abertura. Use
          &ldquo;reproduzir todas&rdquo; para compará-las no mesmo instante.
        </p>
      </div>

      {/* Repeated at the foot of the section: by the time you've read down to
       * the lockups, the button at the top is off-screen. */}
      <div className="mt-8 flex justify-start">
        <PlayAllButton onPlay={playAll} />
      </div>
    </>
  )
}

const DONTS = [
  'Não altere as cores do logotipo — apenas tinta ou branco.',
  'Não estique, rotacione ou distorça o símbolo ou o nome.',
  'Não recrie o nome com outra fonte, peso ou espaçamento.',
  'Não aplique sombras, contornos, gradientes ou efeitos.',
  'Não use o logotipo tinta sobre fundos escuros (nem o branco sobre claros).',
  'Não coloque o logotipo sobre fotos ou fundos com pouco contraste.',
]

function BrandPage() {
  return (
    <MarketingShell>
      <section className="border-[#ececec] border-b bg-gradient-to-b from-white to-[#fafafa] pt-16 pb-20">
        <div className="mx-auto max-w-[1120px] px-6">
          <div className="grid items-center gap-12 md:grid-cols-[1.1fr_1fr]">
            <div>
              <h1 className="mt-3 font-display text-[#0b0b0d] text-[clamp(36px,5.4vw,56px)] leading-[1.04] tracking-[-0.01em]">
                A marca Planno
              </h1>
              <p className="mt-5 max-w-[520px] text-[#6c6c72] text-[clamp(16px,2.2vw,19px)] leading-relaxed">
                Logotipo, cores, tipografia e regras de uso — tudo o que você precisa para
                representar o Planno do jeito certo, em qualquer formato.
              </p>
              {import.meta.env.DEV ? (
                <div className="mt-7">
                  <a
                    className="inline-flex items-center gap-2 rounded-full bg-[#1d1d20] px-6 py-3 font-semibold text-[15px] text-white transition hover:bg-black"
                    download
                    href="/brand/planno-brand.zip"
                  >
                    <IconArrowDownToLineOutline18 className="size-4" />
                    Baixar kit completo (.zip)
                  </a>
                </div>
              ) : null}
            </div>
            <div className="flex justify-center">
              <img
                alt="Logotipo vertical do Planno"
                className="h-[200px] w-auto md:h-[240px]"
                height={708}
                src="/brand/planno-logo-vertical.svg"
                width={758}
              />
            </div>
          </div>
        </div>
      </section>

      <Section
        intro="Planno vem de plano — de projeto e de planejamento. As duas montanhas do símbolo são o terreno onde cada projeto começa."
        title="O nome"
      >
        <ul className="grid gap-3 text-[#3d3d42] text-[15px] leading-relaxed md:grid-cols-2">
          <li className="rounded-2xl border border-[#ececec] p-5">
            Escreva sempre <strong className="text-[#0b0b0d]">Planno</strong> — com P maiúsculo e
            dois enes. Nunca &ldquo;PLANNO&rdquo;, &ldquo;planno&rdquo; ou &ldquo;Plano&rdquo;.
          </li>
          <li className="rounded-2xl border border-[#ececec] p-5">
            Em texto corrido, use a tipografia do próprio texto. O logotipo é para assinaturas
            visuais, não para o meio de frases.
          </li>
        </ul>
      </Section>

      <Section
        intro="Três formas, duas cores. O logotipo existe em tinta (#0B0B0D) para fundos claros e em branco para fundos escuros — nada além disso."
        title="O logotipo"
      >
        <div className="grid gap-6 md:grid-cols-3">
          <LockupCard
            darkSrc="/brand/planno-logo-horizontal-white.svg"
            description="O formato padrão. Símbolo e nome lado a lado, alinhados pela base óptica."
            downloads={[
              { href: '/brand/planno-logo-horizontal.svg', label: 'SVG' },
              { href: '/brand/planno-logo-horizontal.png', label: 'PNG' },
              { href: '/brand/planno-logo-horizontal-white.svg', label: 'SVG branco' },
              { href: '/brand/planno-logo-horizontal-white.png', label: 'PNG branco' },
            ]}
            intrinsic={{ height: 260, width: 1088 }}
            lightSrc="/brand/planno-logo-horizontal.svg"
            name="Horizontal"
            previewClass="h-[40px]"
          />
          <LockupCard
            darkSrc="/brand/planno-logo-vertical-white.svg"
            description="Símbolo sobre o nome, centralizados. Para espaços altos e estreitos."
            downloads={[
              { href: '/brand/planno-logo-vertical.svg', label: 'SVG' },
              { href: '/brand/planno-logo-vertical.png', label: 'PNG' },
              { href: '/brand/planno-logo-vertical-white.svg', label: 'SVG branco' },
              { href: '/brand/planno-logo-vertical-white.png', label: 'PNG branco' },
            ]}
            intrinsic={{ height: 708, width: 758 }}
            lightSrc="/brand/planno-logo-vertical.svg"
            name="Vertical"
            previewClass="h-[120px]"
          />
          <LockupCard
            darkSrc="/brand/planno-glyph-white.svg"
            description="As duas montanhas, sozinhas. Para avatares, favicons e espaços mínimos."
            downloads={[
              { href: '/brand/planno-glyph.svg', label: 'SVG' },
              { href: '/brand/planno-glyph.png', label: 'PNG' },
              { href: '/brand/planno-glyph-white.svg', label: 'SVG branco' },
              { href: '/brand/planno-glyph-white.png', label: 'PNG branco' },
            ]}
            intrinsic={{ height: 440, width: 520 }}
            lightSrc="/brand/planno-glyph.svg"
            name="Símbolo"
            previewClass="h-[72px]"
          />
        </div>
      </Section>

      <Section
        intro="Cada proporção de espaço pede uma forma do logotipo. A regra é simples: horizontal por padrão, vertical quando faltar largura, símbolo quando faltar tudo."
        title="Qual formato usar"
      >
        <div className="grid gap-6 md:grid-cols-3">
          {FORMAT_GUIDES.map((guide) => (
            <div className="rounded-2xl border border-[#ececec] p-6" key={guide.name}>
              <div className="flex h-[140px] items-center justify-center">{guide.example}</div>
              <h3 className="mt-4 font-semibold text-[#0b0b0d] text-[15px]">{guide.name}</h3>
              <p className="mt-1.5 text-[#6c6c72] text-[13.5px] leading-relaxed">{guide.body}</p>
              <p className="mt-3 font-medium text-[#0b0b0d] text-[13px]">{guide.spec}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-[#ececec] p-6">
            <h3 className="font-semibold text-[#0b0b0d] text-[15px]">Área de proteção</h3>
            <p className="mt-1.5 text-[#6c6c72] text-[13.5px] leading-relaxed">
              Mantenha livre, em todos os lados, um espaço equivalente à metade da altura do
              símbolo. Nada de texto, bordas ou outros logotipos dentro dessa área.
            </p>
            <div className="mt-5 flex justify-center">
              <div className="rounded-xl border border-[#d9d9de] border-dashed p-8">
                <img
                  alt=""
                  className="h-[36px] w-auto"
                  height={260}
                  src="/brand/planno-logo-horizontal.svg"
                  width={1088}
                />
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-[#ececec] p-6">
            <h3 className="font-semibold text-[#0b0b0d] text-[15px]">Tamanho mínimo</h3>
            <p className="mt-1.5 text-[#6c6c72] text-[13.5px] leading-relaxed">
              Abaixo disso a marca perde definição: horizontal com pelo menos 96&nbsp;px de largura,
              vertical com 64&nbsp;px de largura e símbolo com 16&nbsp;px de altura.
            </p>
            <div className="mt-5 flex items-end justify-center gap-8">
              <img
                alt=""
                className="w-[96px]"
                height={260}
                src="/brand/planno-logo-horizontal.svg"
                width={1088}
              />
              <img
                alt=""
                className="w-[64px]"
                height={708}
                src="/brand/planno-logo-vertical.svg"
                width={758}
              />
              <img
                alt=""
                className="h-[16px] w-auto"
                height={440}
                src="/brand/planno-glyph.svg"
                width={520}
              />
            </div>
          </div>
        </div>
      </Section>

      <Section
        intro="Exploração — nada aqui faz parte do kit ainda. Seis maneiras de a marca entrar em cena, todas terminando no logotipo exato desta página: nenhuma muda cor, proporção ou espaçamento no estado final. Passe o mouse ou clique em qualquer uma para rever."
        title="Movimento"
      >
        <MotionSection />
      </Section>

      <Section
        intro="Tudo o que o Planno gera assina com a mesma marca: o símbolo do kit, intacto, com duas faíscas por cima no canto superior direito. A faísca de quatro pontas é o clichê da categoria — e é por isso que ela entra, porque é lida sem pensar. O que passa a ser nosso é o par, não a faísca."
        title="Inteligência"
      >
        <AiSection />
      </Section>

      <Section
        intro="Preto no branco, na marca e no produto. O único acento fixo é o azul, reservado para estados selecionados — todo o resto da cor no app é semântico: status, alertas, gráficos. Nada de cor dentro do logotipo."
        title="Cores"
      >
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {BRAND_COLORS.map((color) => (
            <ColorCard key={color.hex} {...color} />
          ))}
        </div>
      </Section>

      <Section
        intro="Duas famílias, papéis claros: uma para a voz da marca, outra para o dia a dia da interface."
        title="Tipografia"
      >
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-[#ececec] p-8">
            <p className="font-display font-semibold text-[#0b0b0d] text-[44px] leading-tight tracking-[-0.01em]">
              Poppins
            </p>
            <p className="mt-1 font-display text-[#6c6c72] text-[20px]">AaBbCc — 0123456789</p>
            <p className="mt-5 text-[#6c6c72] text-[13.5px] leading-relaxed">
              A voz da marca. Usada no nome do logotipo, em títulos do produto e do site e em
              momentos de destaque. Do extralight ao bold — o contraste de pesos faz parte da voz.
            </p>
          </div>
          <div className="rounded-2xl border border-[#ececec] p-8">
            <p className="font-sans font-semibold text-[#0b0b0d] text-[44px] leading-tight tracking-[-0.02em]">
              Figtree
            </p>
            <p className="mt-1 font-sans text-[#6c6c72] text-[20px]">AaBbCc — 0123456789</p>
            <p className="mt-5 text-[#6c6c72] text-[13.5px] leading-relaxed">
              O texto de todo o resto: interface do produto, parágrafos, botões e legendas.
              Variável, do regular ao bold.
            </p>
          </div>
        </div>
      </Section>

      <Section
        intro="Quando alguém compartilha planno.app, esta é a imagem que aparece — em conversas, redes sociais e resultados de busca. Ela vive em /og-image.png e segue a tipografia da marca."
        title="Compartilhamento"
      >
        <div className="overflow-hidden rounded-2xl border border-[#ececec]">
          <img
            alt="Cartão de compartilhamento do Planno — logotipo, título e módulos"
            className="w-full"
            height={630}
            src="/og-image.png"
            width={1200}
          />
        </div>
        <div className="mt-4">
          <DownloadLink href="/og-image.png" label="PNG (1200×630)" />
        </div>

        {/* How the card unfurls in the wild. Titles and description mirror
         * the homepage OG meta in `src/routes/index.tsx` — update both
         * together. */}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="overflow-hidden rounded-2xl border border-[#ececec]">
            <div className="bg-[#efeae2] px-6 py-8">
              <div className="ml-auto max-w-[340px] rounded-lg rounded-tr-none bg-[#d9fdd3] p-1.5 shadow-[0_1px_1px_rgba(0,0,0,0.08)]">
                <div className="overflow-hidden rounded-md bg-[#d1f4cc]">
                  <img
                    alt="Prévia do link do Planno em um aplicativo de mensagens"
                    className="w-full"
                    height={630}
                    src="/og-image.png"
                    width={1200}
                  />
                  <div className="px-2.5 py-2">
                    <p className="font-semibold text-[#111b21] text-[13px] leading-snug">
                      Planno — o escritório inteiro em um só lugar
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[#54656f] text-[12px] leading-snug">
                      O Planno reúne projetos, tarefas, sprints, visitas técnicas e clientes do seu
                      escritório de arquitetura e engenharia em um só lugar.
                    </p>
                    <p className="mt-0.5 text-[#8696a0] text-[11px]">planno.app</p>
                  </div>
                </div>
                <p className="px-1 pt-1 text-[#027eb5] text-[13.5px]">https://planno.app</p>
                <p className="pr-1 text-right text-[#667781] text-[10.5px]">14:32 ✓✓</p>
              </div>
            </div>
            <p className="border-[#ececec] border-t px-5 py-3 text-[#6c6c72] text-[12.5px]">
              Em aplicativos de mensagens (WhatsApp, Telegram)
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-[#ececec]">
            <div className="bg-white px-6 py-8">
              <div className="mx-auto max-w-[420px]">
                <div className="flex items-center gap-2.5">
                  <img
                    alt=""
                    className="size-10 rounded-full"
                    height={1024}
                    src="/brand/planno-icon-square-dark.svg"
                    width={1024}
                  />
                  <div className="leading-tight">
                    <p className="font-semibold text-[#0f1419] text-[14px]">Planno</p>
                    <p className="text-[#536471] text-[13px]">@plannoapp · 2 h</p>
                  </div>
                </div>
                <p className="mt-2 text-[#0f1419] text-[14.5px] leading-snug">
                  Do briefing à entrega das chaves, reunido em um só lugar. 🏔️
                </p>
                <div className="relative mt-3 overflow-hidden rounded-2xl border border-[#cfd9de]">
                  <img
                    alt="Prévia do link do Planno em uma publicação no X"
                    className="w-full"
                    height={630}
                    src="/og-image.png"
                    width={1200}
                  />
                  <span className="absolute bottom-2 left-2 rounded bg-black/70 px-1.5 py-0.5 text-[11px] text-white">
                    planno.app
                  </span>
                </div>
              </div>
            </div>
            <p className="border-[#ececec] border-t px-5 py-3 text-[#6c6c72] text-[12.5px]">
              Em redes sociais (X, LinkedIn)
            </p>
          </div>
        </div>
      </Section>

      <Section
        intro="Se uma aplicação precisar quebrar alguma destas regras, fale com a gente antes."
        title="O que não fazer"
      >
        <ul className="grid gap-3 md:grid-cols-2">
          {DONTS.map((dont) => (
            <li
              className="rounded-2xl border border-[#ececec] p-5 text-[#3d3d42] text-[14.5px] leading-relaxed"
              key={dont}
            >
              {dont}
            </li>
          ))}
        </ul>
      </Section>
    </MarketingShell>
  )
}
